/**
 * A linear market: demand and supply straight lines, equilibrium, consumer and producer
 * surplus, and the effect of a per-unit tax or subsidy.
 *
 * Everything is kept in "price form", the way a supply and demand graph is drawn with
 * price on the vertical axis:
 *
 *   inverse demand  P = α − βQ   (α = the highest price anyone pays, β > 0)
 *   inverse supply  P = γ + δQ   (γ = the price intercept, may be negative, δ > 0)
 *
 * The quantity form many courses use, Qd = a − bP and Qs = c + dP, converts to this with
 * α = a ÷ b, β = 1 ÷ b, γ = −c ÷ d, δ = 1 ÷ d.
 *
 * A per-unit tax t puts a wedge of t between what buyers pay (Pb) and what sellers keep
 * (Ps = Pb − t); a subsidy is a negative tax. Who the law says pays the tax does not
 * change any of these numbers.
 */

export interface Curves {
  /** α: the demand line's price intercept. */
  demandIntercept: number;
  /** β: how far price falls per extra unit demanded. */
  demandSlope: number;
  /** γ: the supply line's price intercept. */
  supplyIntercept: number;
  /** δ: how far price rises per extra unit supplied. */
  supplySlope: number;
}

/** Qd = a − bP and Qs = c + dP, with b > 0 and d > 0. */
export function fromQuantityForm(
  a: number,
  b: number,
  c: number,
  d: number
): Curves | null {
  if (!(b > 0 && d > 0)) {
    return null;
  }
  return {
    demandIntercept: a / b,
    demandSlope: 1 / b,
    supplyIntercept: -c / d,
    supplySlope: 1 / d,
  };
}

/** P = a − bQ and P = c + dQ, with b > 0 and d > 0. */
export function fromPriceForm(
  a: number,
  b: number,
  c: number,
  d: number
): Curves | null {
  if (!(b > 0 && d > 0)) {
    return null;
  }
  return {
    demandIntercept: a,
    demandSlope: b,
    supplyIntercept: c,
    supplySlope: d,
  };
}

export interface Equilibrium {
  quantity: number;
  price: number;
  consumerSurplus: number;
  producerSurplus: number;
  totalSurplus: number;
  /** |Ed| and Es at equilibrium: (1 ÷ slope) × (P ÷ Q). */
  demandElasticity: number;
  supplyElasticity: number;
}

export type MarketFailure = "no-trade" | "negative-price";

/**
 * The equilibrium, or why there is none: the demand line starts at or below the supply
 * line (no unit is worth more to a buyer than it costs a seller), or the lines cross at
 * a negative price.
 */
export function equilibrium(curves: Curves): Equilibrium | MarketFailure {
  const { demandIntercept: a, demandSlope: b } = curves;
  const { supplyIntercept: c, supplySlope: d } = curves;
  if (!(a > c)) {
    return "no-trade";
  }
  const quantity = (a - c) / (b + d);
  const price = a - b * quantity;
  if (price < 0) {
    return "negative-price";
  }
  const consumerSurplus = 0.5 * (a - price) * quantity;
  const producerSurplus = 0.5 * (price - c) * quantity;
  return {
    quantity,
    price,
    consumerSurplus,
    producerSurplus,
    totalSurplus: consumerSurplus + producerSurplus,
    demandElasticity: price / (b * quantity),
    supplyElasticity: price / (d * quantity),
  };
}

export interface Intervention {
  /** t: positive for a tax, negative for a subsidy. */
  perUnit: number;
  quantity: number;
  /** Pb: what buyers pay per unit. */
  buyerPrice: number;
  /** Ps: what sellers receive per unit, Pb − t. */
  sellerPrice: number;
  /** Buyers' share of the per-unit tax (or benefit of the subsidy): β ÷ (β + δ). */
  buyerShare: number;
  sellerShare: number;
  /** t × Q: tax revenue, or minus the subsidy bill. */
  governmentRevenue: number;
  consumerSurplus: number;
  producerSurplus: number;
  /** Surplus lost: ½ × |t| × |Q* − Q|. */
  deadweightLoss: number;
}

export type InterventionFailure = "prohibitive" | "negative-buyer-price";

/** The market after a per-unit tax (t > 0) or subsidy (t < 0). */
export function withIntervention(
  curves: Curves,
  before: Equilibrium,
  perUnit: number
): Intervention | InterventionFailure {
  const { demandIntercept: a, demandSlope: b } = curves;
  const { supplyIntercept: c, supplySlope: d } = curves;
  const quantity = (a - c - perUnit) / (b + d);
  if (!(quantity > 0)) {
    return "prohibitive";
  }
  const buyerPrice = a - b * quantity;
  if (buyerPrice < 0) {
    return "negative-buyer-price";
  }
  const sellerPrice = buyerPrice - perUnit;
  const consumerSurplus = 0.5 * (a - buyerPrice) * quantity;
  const producerSurplus = 0.5 * (sellerPrice - c) * quantity;
  const governmentRevenue = perUnit * quantity;
  return {
    perUnit,
    quantity,
    buyerPrice,
    sellerPrice,
    buyerShare: b / (b + d),
    sellerShare: d / (b + d),
    governmentRevenue,
    consumerSurplus,
    producerSurplus,
    deadweightLoss:
      before.totalSurplus -
      (consumerSurplus + producerSurplus + governmentRevenue),
  };
}

export type SolveError = "bad-slope" | MarketFailure | InterventionFailure;

export type Solved =
  | { error: SolveError }
  | {
      error: null;
      curves: Curves;
      market: Equilibrium;
      after: Intervention | null;
    };

/**
 * Curves in either form to a solved market, with the per-unit tax (positive) or subsidy
 * (negative) applied when `perUnit` is not zero.
 */
export function solveMarket(
  form: "quantity" | "price",
  numbers: { a: number; b: number; c: number; d: number },
  perUnit: number
): Solved {
  const { a, b, c, d } = numbers;
  const curves = (form === "quantity" ? fromQuantityForm : fromPriceForm)(
    a,
    b,
    c,
    d
  );
  if (!curves) {
    return { error: "bad-slope" };
  }
  const market = equilibrium(curves);
  if (typeof market === "string") {
    return { error: market };
  }
  if (perUnit === 0) {
    return { error: null, curves, market, after: null };
  }
  const after = withIntervention(curves, market, perUnit);
  if (typeof after === "string") {
    return { error: after };
  }
  return { error: null, curves, market, after };
}
